import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Wrench, CheckCircle, Clock, AlertCircle, TrendingUp, 
  Package, Target, Award, Bell
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EngineerDashboard = ({ user }) => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboard();
    // Refresh every 30 seconds
    const interval = setInterval(loadDashboard, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const engineerId = user?.employee_id || 'ENG001'; // Get from user context
      
      const response = await axios.get(
        `${API_URL}/api/trc/engineer/${engineerId}/dashboard`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setDashboard(response.data);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const metrics = dashboard?.metrics || {};
  const devices = dashboard?.devices || [];
  const sosDevices = devices.filter(d => d.is_sos && d.status !== 'dispatched');

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Wrench className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Engineer Dashboard</h1>
          </div>
          <button
            onClick={loadDashboard}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Refresh
          </button>
        </div>

        {/* SOS Alert */}
        {sosDevices.length > 0 && (
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-300 rounded-lg flex items-center gap-3">
            <Bell className="w-6 h-6 text-red-600" />
            <div className="flex-1">
              <p className="font-bold text-red-900">SOS Priority Devices!</p>
              <p className="text-sm text-red-700">{sosDevices.length} devices need urgent attention</p>
            </div>
            <button
              onClick={() => navigate('/engineer/devices?filter=sos')}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              View SOS
            </button>
          </div>
        )}

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {/* Total Assigned */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <Package className="w-8 h-8 text-blue-600" />
              <span className="text-3xl font-bold text-gray-900">{metrics.total_assigned || 0}</span>
            </div>
            <p className="text-sm text-gray-600">Total Assigned</p>
            <p className="text-xs text-gray-500 mt-1">Max: {metrics.max_capacity || 25}</p>
          </div>

          {/* Pending Repairs */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-8 h-8 text-orange-600" />
              <span className="text-3xl font-bold text-gray-900">{metrics.pending_repairs || 0}</span>
            </div>
            <p className="text-sm text-gray-600">Pending Repairs</p>
            <p className="text-xs text-gray-500 mt-1">In queue</p>
          </div>

          {/* Completed Today */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <span className="text-3xl font-bold text-gray-900">{metrics.completed_today || 0}</span>
            </div>
            <p className="text-sm text-gray-600">Completed Today</p>
            <p className="text-xs text-gray-500 mt-1">Keep going!</p>
          </div>

          {/* FQC Pass Rate */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <Award className="w-8 h-8 text-purple-600" />
              <span className="text-3xl font-bold text-gray-900">{metrics.fqc_pass_rate || 0}%</span>
            </div>
            <p className="text-sm text-gray-600">FQC Pass Rate</p>
            <p className="text-xs text-gray-500 mt-1">First-time fix</p>
          </div>
        </div>

        {/* Capacity Bar */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-gray-900">Workload Capacity</h2>
            <span className="text-sm text-gray-600">
              {metrics.remaining_capacity || 0} slots remaining
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div
              className={`h-4 rounded-full transition-all ${
                (metrics.total_assigned / metrics.max_capacity) * 100 > 80
                  ? 'bg-red-600'
                  : (metrics.total_assigned / metrics.max_capacity) * 100 > 60
                  ? 'bg-orange-600'
                  : 'bg-green-600'
              }`}
              style={{ width: `${(metrics.total_assigned / metrics.max_capacity) * 100}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {metrics.total_assigned} / {metrics.max_capacity} devices assigned
          </p>
        </div>

        {/* Recent Devices */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">My Devices</h2>
            <button
              onClick={() => navigate('/engineer/devices')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              View All
            </button>
          </div>

          {devices.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Package className="w-16 h-16 mx-auto mb-3 text-gray-300" />
              <p>No devices assigned yet</p>
            </div>
          ) : (
            <div className="divide-y">
              {devices.slice(0, 5).map((device, index) => (
                <div
                  key={index}
                  onClick={() => navigate(`/engineer/repair/${device.trc_device_id}`)}
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900">{device.did}</span>
                        {device.is_sos && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs font-semibold rounded">
                            SOS
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">
                        {device.brand} {device.model}
                      </p>
                      <p className="text-xs text-gray-500">{device.trc_device_id}</p>
                    </div>
                    <div className="text-right">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        device.status === 'rfr'
                          ? 'bg-green-100 text-green-800'
                          : device.status === 'repair_in_progress'
                          ? 'bg-blue-100 text-blue-800'
                          : device.status === 're_pna'
                          ? 'bg-orange-100 text-orange-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {device.status.replace(/_/g, ' ').toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EngineerDashboard;
